import { useState } from 'react';
import { message } from 'antd';
import { SendIcon } from '../assets/icons';
import { useAppSelector } from '../app/hooks';
import {
  useGetCommentsQuery,
  useCreateCommentMutation,
  commentUserName,
  commentText,
  commentWhen,
  type EntityType,
} from '../features/activity/activityApi';

/** Two-letter initials for an avatar (the API carries no photo). */
function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-shade text-xs font-semibold text-brand">
      {initials(name)}
    </span>
  );
}

/** Comments for an entity (e.g. a Task/Subtask), backed by /activity/comment/. */
export default function CommentsPanel({
  entityId,
  entityType = 'Task',
}: {
  entityId: number | string;
  entityType?: EntityType;
}) {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const userName = useAppSelector((s) => s.auth.user?.name) || 'You';
  const { data: comments, isFetching } = useGetCommentsQuery({ entityType, entityId });
  const [createComment, { isLoading }] = useCreateCommentMutation();
  const [text, setText] = useState('');

  const list = comments ?? [];

  const submit = async () => {
    const c = text.trim();
    if (!c) return;
    if (!userId) {
      message.error('Your session has no user id — please sign in again.');
      return;
    }
    try {
      await createComment({
        entity_type: entityType,
        entity_id: Number(entityId),
        user: userId,
        comment: c,
      }).unwrap();
      setText('');
    } catch {
      message.error('Could not post the comment. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-6 rounded-xl border border-line-soft bg-white p-6 shadow-card">
      <h2 className="text-base font-semibold text-ink">Comments</h2>

      {list.length === 0 ? (
        <p className="text-sm text-ink-secondary">
          {isFetching ? 'Loading comments…' : 'No comments yet — start the conversation.'}
        </p>
      ) : (
        list.map((c) => (
          <div key={c.id} className="flex gap-4">
            <Avatar name={commentUserName(c)} />
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">{commentUserName(c)}</span>
                <span className="text-xs font-medium text-ink-secondary">{commentWhen(c)}</span>
              </div>
              <div className="rounded-lg border border-[#f3f4f6] bg-[#f8fafc] p-4 text-sm leading-[22px] text-ink">
                {commentText(c)}
              </div>
            </div>
          </div>
        ))
      )}

      {/* Add a comment */}
      <div className="flex gap-4">
        <Avatar name={userName} />
        <div className="relative flex-1">
          <textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="Add a comment or update…"
            className="w-full resize-none rounded-lg border border-[#e5e7eb] bg-[#f8fafc] p-4 pr-14 text-sm text-ink placeholder:text-ink-secondary focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
          <button
            type="button"
            aria-label="Send comment"
            onClick={() => void submit()}
            disabled={isLoading || !text.trim()}
            className="absolute bottom-3 right-3 flex size-8 items-center justify-center rounded-lg bg-brand text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendIcon className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
